import { call, put, takeEvery, all, fork } from "redux-saga/effects";
import { api } from "../services/apiService";
import type from "../actions/types";
import { isApiFailure, isUpdateApiFailure, apiFailureStatus } from "./apiFailure";

// Every saga here except getAbsiteProgramSummary (already fixed) dispatched
// SUCCESS UNCONDITIONALLY on resolve — but the fetchers never reject (a failure
// resolves {status}/{errStatus}), so the catch/ERROR branch was dead code and
// every failure paraded as SUCCESS (#603 gate finding 6). Each now classifies
// the resolve; the ERROR reducers map `status` → an error message
// (reducers/admin.js).
// Predicate per fetcher family (#612 gate): doGetCall-backed → isApiFailure;
// doUpdateCall/doDeleteCall-backed → isUpdateApiFailure (errStatus ONLY —
// real success bodies here carry domain top-level status: Plan.status
// "ACTIVE", BookingRefund.status, EnrollmentInvitation.status).

function* getInvitations() {
    try {
        const invitationRes = yield call(api.getInvitations);
        if (isApiFailure(invitationRes)) {
            yield put({ type: type.GET_INVITATION_ERROR, status: apiFailureStatus(invitationRes) });
        } else {
            yield put({
                type: type.GET_INVITATION_SUCCESS,
                invitationList: invitationRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_INVITATION_ERROR, message: e.message });
    }
}

function* sendInvitation(action) {
    try {
        const sendInvitationRes = yield call(api.sendInvitation, action.payload);
        if (isUpdateApiFailure(sendInvitationRes)) {
            yield put({ type: type.SEND_INVITATION_ERROR, status: apiFailureStatus(sendInvitationRes) });
        } else {
            yield put({
                type: type.SEND_INVITATION_SUCCESS,
                sendInvitation: sendInvitationRes
            });
        }
    } catch (e) {
        yield put({ type: type.SEND_INVITATION_ERROR, message: e.message });
    }
}

function* resendInvitation(action) {
    try {
        const resendInvitationRes = yield call(api.resendInvitation, action.payload);
        // EnrollmentInvitation.status (InvitationStatus) rides genuine success.
        if (isUpdateApiFailure(resendInvitationRes)) {
            yield put({ type: type.RESEND_INVITATION_ERROR, status: apiFailureStatus(resendInvitationRes) });
        } else {
            yield put({
                type: type.RESEND_INVITATION_SUCCESS,
                resendInvitation: resendInvitationRes
            });
        }
    } catch (e) {
        yield put({ type: type.RESEND_INVITATION_ERROR, message: e.message });
    }
}

function* getComment(action) {
    try {
        const commentRes = yield call(api.getComment, action.payload);
        if (isApiFailure(commentRes)) {
            yield put({ type: type.GET_COMMENT_ERROR, status: apiFailureStatus(commentRes) });
        } else {
            yield put({
                type: type.GET_COMMENT_SUCCESS,
                comments: commentRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_COMMENT_ERROR, message: e.message });
    }
}

function* setComment(action) {
    try {
        const commentRes = yield call(api.setComment, action.payload);
        if (isUpdateApiFailure(commentRes)) {
            yield put({ type: type.SET_COMMENT_ERROR, status: apiFailureStatus(commentRes) });
        } else {
            yield put({
                type: type.SET_COMMENT_SUCCESS,
                comments: commentRes
            });
        }
    } catch (e) {
        yield put({ type: type.SET_COMMENT_ERROR, message: e.message });
    }
}

function* getTutorFeedbackAll() {
    try {
        const feedbackRes = yield call(api.getTutorFeedbackAll);
        if (isApiFailure(feedbackRes)) {
            yield put({ type: type.GET_TUTOR_FEEDBACK_ALL_ERROR, status: apiFailureStatus(feedbackRes) });
        } else {
            yield put({
                type: type.GET_TUTOR_FEEDBACK_ALL_SUCCESS,
                feedbacks: feedbackRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_TUTOR_FEEDBACK_ALL_ERROR, message: e.message });
    }
}

function* getStudentFeedbackAll() {
    try {
        const feedbackRes = yield call(api.getStudentFeedbackAll);
        if (isApiFailure(feedbackRes)) {
            yield put({ type: type.GET_STUDENT_FEEDBACK_ALL_ERROR, status: apiFailureStatus(feedbackRes) });
        } else {
            yield put({
                type: type.GET_STUDENT_FEEDBACK_ALL_SUCCESS,
                feedbacks: feedbackRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_STUDENT_FEEDBACK_ALL_ERROR, message: e.message });
    }
}

function* getAbsiteProgramSummary(action) {
    try {
        const summaryRes = yield call(api.getAbsiteProgramSummary, action.payload);
        if (isApiFailure(summaryRes)) {
            yield put({
                type: type.GET_ABSITE_PROGRAM_SUMMARY_ERROR,
                status: apiFailureStatus(summaryRes),
                errMsg: summaryRes?.errMsg?.message
            });
        } else {
            yield put({
                type: type.GET_ABSITE_PROGRAM_SUMMARY_SUCCESS,
                programSummary: summaryRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_ABSITE_PROGRAM_SUMMARY_ERROR, message: e.message });
    }
}

function* getAllPlans() {
    try {
        const planRes = yield call(api.getAllPlans);
        if (isApiFailure(planRes)) {
            yield put({ type: type.GET_ALL_PLANS_ERROR, status: apiFailureStatus(planRes) });
        } else {
            yield put({
                type: type.GET_ALL_PLANS_SUCCESS,
                plans: planRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_ALL_PLANS_ERROR, message: e.message });
    }
}
function* createPlan(action) {
    try {
        const planRes = yield call(api.createPlan, action.payload);
        // Plan.status (PlanStatus, e.g. "ACTIVE") rides genuine success.
        if (isUpdateApiFailure(planRes)) {
            yield put({ type: type.CREATE_PLAN_ERROR, status: apiFailureStatus(planRes) });
        } else {
            yield put({
                type: type.CREATE_PLAN_SUCCESS,
                plan: planRes
            });
        }
    } catch (e) {
        yield put({ type: type.CREATE_PLAN_ERROR, message: e.message });
    }
}
function* updatePlan(action) {
    try {
        const planRes = yield call(api.updatePlan, action.payload);
        if (isUpdateApiFailure(planRes)) {
            yield put({ type: type.UPDATE_PLAN_ERROR, status: apiFailureStatus(planRes) });
        } else {
            yield put({
                type: type.UPDATE_PLAN_SUCCESS,
                plan: planRes
            });
        }
    } catch (e) {
        yield put({ type: type.UPDATE_PLAN_ERROR, message: e.message });
    }
}
function* deletePlan(action) {
    try {
        const planRes = yield call(api.deletePlan, action.payload);
        // doDeleteCall-backed; DELETE /plan/{id} returns the Plan JSON.
        if (isUpdateApiFailure(planRes)) {
            yield put({ type: type.DELETE_PLAN_ERROR, status: apiFailureStatus(planRes) });
        } else {
            yield put({
                type: type.DELETE_PLAN_SUCCESS,
                plan: planRes
            });
        }
    } catch (e) {
        yield put({ type: type.DELETE_PLAN_ERROR, message: e.message });
    }
}

function* sendInvites(action) {
    try {
        const sendInvitationRes = yield call(api.sendInvites, action.payload);
        // EnrollmentInvitation.status (InvitationStatus) rides genuine success.
        if (isUpdateApiFailure(sendInvitationRes)) {
            yield put({ type: type.SEND_INVITES_ERROR, status: apiFailureStatus(sendInvitationRes) });
        } else {
            yield put({
                type: type.SEND_INVITES_SUCCESS,
                sendInvites: sendInvitationRes
            });
        }
    } catch (e) {
        yield put({ type: type.SEND_INVITES_ERROR, message: e.message });
    }
}

function* getUsersByRole() {
    try {
        const usersByRoleRes = yield call(api.getUsersByRole);
        if (isApiFailure(usersByRoleRes)) {
            yield put({ type: type.GET_USERS_BY_ROLE_ERROR, status: apiFailureStatus(usersByRoleRes) });
        } else {
            yield put({
                type: type.GET_USERS_BY_ROLE_SUCCESS,
                usersByRole: usersByRoleRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_USERS_BY_ROLE_ERROR, message: e.message });
    }
}

function* getPaymentsByUser(action) {
    try {
        const paymentsByUserRes = yield call(api.getPaymentsByUser, action.payload);
        if (isApiFailure(paymentsByUserRes)) {
            yield put({ type: type.GET_PAYMENTS_BY_USER_ERROR, status: apiFailureStatus(paymentsByUserRes) });
        } else {
            yield put({
                type: type.GET_PAYMENTS_BY_USER_SUCCESS,
                paymentsByUser: paymentsByUserRes
            });
        }
    } catch (e) {
        yield put({ type: type.GET_PAYMENTS_BY_USER_ERROR, message: e.message });
    }
}

function* refundBooking(action) {
    try {
        const refundBookingRes = yield call(api.refundBooking, action.payload);
        // BookingRefund.status (RefundStatus) rides genuine success — error-on-
        // success here invites double-refund retries (real-money path).
        if (isUpdateApiFailure(refundBookingRes)) {
            yield put({ type: type.SET_REFUND_BOOKING_ERROR, status: apiFailureStatus(refundBookingRes) });
        } else {
            yield put({
                type: type.SET_REFUND_BOOKING_SUCCESS,
                refundBooking: refundBookingRes
            });
        }
    } catch (e) {
        yield put({ type: type.SET_REFUND_BOOKING_ERROR, message: e.message });
    }
}

// Test seam: failure classification for every admin saga (api never rejects).
export const testable = {
    getInvitations,
    sendInvitation,
    resendInvitation,
    getComment,
    setComment,
    getTutorFeedbackAll,
    getStudentFeedbackAll,
    getAbsiteProgramSummary,
    getAllPlans,
    createPlan,
    updatePlan,
    deletePlan,
    sendInvites,
    getUsersByRole,
    getPaymentsByUser,
    refundBooking,
};

function* watchGetInvitations() {
    yield takeEvery(type.GET_INVITATION_REQUEST, getInvitations);
}

function* watchSendInvitation() {
    yield takeEvery(type.SEND_INVITATION_REQUEST, sendInvitation);
}

function* watchResendInvitation() {
    yield takeEvery(type.RESEND_INVITATION_REQUEST, resendInvitation);
}

function* watchGetComment() {
    yield takeEvery(type.GET_COMMENT_REQUEST, getComment);
}

function* watchSetComment() {
    yield takeEvery(type.SET_COMMENT_REQUEST, setComment);
}

function* watchGetTutorFeedbackAll() {
    yield takeEvery(type.GET_TUTOR_FEEDBACK_ALL_REQUEST, getTutorFeedbackAll);
}

function* watchGetStudentFeedbackAll() {
    yield takeEvery(type.GET_STUDENT_FEEDBACK_ALL_REQUEST, getStudentFeedbackAll);
}

function* watchGetAbsiteProgramSummary() {
    yield takeEvery(type.GET_ABSITE_PROGRAM_SUMMARY_REQUEST, getAbsiteProgramSummary);
}

function* watchGetAllPlans() {
    yield takeEvery(type.GET_ALL_PLANS_REQUEST, getAllPlans);
}

function* watchCreatePlan() {
    yield takeEvery(type.CREATE_PLAN_REQUEST, createPlan);
}

function* watchUpdatePlan() {
    yield takeEvery(type.UPDATE_PLAN_REQUEST, updatePlan);
}

function* watchDeletePlan() {
    yield takeEvery(type.DELETE_PLAN_REQUEST, deletePlan);
}

function* watchSendInvites() {
    yield takeEvery(type.SEND_INVITES_REQUEST, sendInvites);
}

function* watchGetUsersByRole() {
    yield takeEvery(type.GET_USERS_BY_ROLE_REQUEST, getUsersByRole);
}

function* watchGetPaymentsByUser() {
    yield takeEvery(type.GET_PAYMENTS_BY_USER_REQUEST, getPaymentsByUser);
}

function* watchRefundBooking() {
    yield takeEvery(type.SET_REFUND_BOOKING_REQUEST, refundBooking);
}

export default function* adminSaga() {
    yield all([
        fork(watchGetInvitations),
        fork(watchSendInvitation),
        fork(watchResendInvitation),
        fork(watchGetComment),
        fork(watchSetComment),
        fork(watchGetTutorFeedbackAll),
        fork(watchGetStudentFeedbackAll),
        fork(watchGetAbsiteProgramSummary),
        fork(watchGetAllPlans),
        fork(watchCreatePlan),
        fork(watchUpdatePlan),
        fork(watchDeletePlan),
        fork(watchSendInvites),
        fork(watchGetUsersByRole),
        fork(watchGetPaymentsByUser),
        fork(watchRefundBooking),
    ]);
}
